import type { Result } from '@de/types/result';

/**
 * Treats whitespace-only and empty strings (often from a missing
 * `{{...}}` interpolation) as absent.
 */
function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Validates that a value can open the governed lead flow.
 *
 * A finite, positive `programId` is required in every case: the drawer
 * renders `selectedResult.programId.toString()` as a DOM id and the dedupe
 * registry keys on it, so a non-numeric, non-finite, or sentinel (`0`)
 * `programId` would crash or collide in the registry. This also makes the
 * `0` fallback `normalizeOfferingsMatchItem` emits for a missing/non-numeric
 * `externalId` correctly non-submittable.
 *
 * Beyond that, a result qualifies via **either** identity:
 *
 *   - **Forward (`/match`) identity** — a non-empty `offeringUuid` **and**
 *     `organizationId`. New `normalizeOfferingsMatchItem`-produced results
 *     carry the canonical offering UUID, the owning organization UUID, and a
 *     best-effort numeric `programId`. The v4 lead flow (LDS v4 diffy/submit)
 *     keys on `organizationId`, so a `/match` card missing it — e.g. an
 *     offering with no linked organization, which Offerings can return as an
 *     empty `organizationIds` — is not submittable and must not open the
 *     drawer; without this guard it would pass the gate and then throw at
 *     submit time. The by-id detail source still enriches sparse *school*
 *     identity once the drawer is open.
 *   - **Legacy identity** — the full `schoolId` / `schoolSlug` /
 *     `schoolName` / `programName` quartet the v3 submit + duplicate gate
 *     rely on. Preserves the original `resultsMapper` contract unchanged.
 *
 * This rejects hand-built or partial payloads (e.g. `{ programId: 888 }`)
 * that carry neither identity. It only verifies shape, not that the id
 * matches the approved result source.
 */
export function isSubmittableResult(value: unknown): value is Result {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<
    Pick<
      Result,
      | 'offeringUuid'
      | 'organizationId'
      | 'programId'
      | 'schoolId'
      | 'schoolSlug'
      | 'schoolName'
      | 'programName'
    >
  >;
  if (
    typeof candidate.programId !== 'number' ||
    !Number.isFinite(candidate.programId) ||
    candidate.programId <= 0
  ) {
    return false;
  }
  const hasForwardIdentity =
    isNonEmptyString(candidate.offeringUuid) &&
    isNonEmptyString(candidate.organizationId);
  const hasLegacyIdentity =
    isNonEmptyString(candidate.schoolId) &&
    isNonEmptyString(candidate.schoolSlug) &&
    isNonEmptyString(candidate.schoolName) &&
    isNonEmptyString(candidate.programName);
  return hasForwardIdentity || hasLegacyIdentity;
}
